import { MaterialDateConfirmationMilestone } from "../types";

export type ProjectedDateValidationOptions = {
  allowEmptyProjectedDate?: boolean;
};

export function getMilestoneProjectedDateBaseline(
  milestone: Pick<
    MaterialDateConfirmationMilestone,
    "projectedDate" | "actualDate"
  >
): string | null {
  return milestone.actualDate ?? milestone.projectedDate ?? null;
}

export function getMaximumUpstreamProjectedDateBaseline(
  milestones: Pick<
    MaterialDateConfirmationMilestone,
    "projectedDate" | "actualDate"
  >[],
  index: number
): string | null {
  let maximumBaseline: string | null = null;

  for (let upstreamIndex = 0; upstreamIndex < index; upstreamIndex += 1) {
    const baseline = getMilestoneProjectedDateBaseline(
      milestones[upstreamIndex]
    );
    if (baseline && (!maximumBaseline || baseline > maximumBaseline)) {
      maximumBaseline = baseline;
    }
  }

  return maximumBaseline;
}

export function isMilestoneProjectedDateInvalid(
  milestone: Pick<MaterialDateConfirmationMilestone, "projectedDate">,
  maximumUpstreamBaseline: string | null,
  options: ProjectedDateValidationOptions = {}
): boolean {
  if (!milestone.projectedDate) {
    if (options.allowEmptyProjectedDate) {
      return false;
    }
    // Duration-driven: missing date is only an order error when an earlier
    // milestone already defines a baseline to compare against.
    return Boolean(maximumUpstreamBaseline);
  }

  if (!maximumUpstreamBaseline) {
    return false;
  }

  return milestone.projectedDate < maximumUpstreamBaseline;
}

export function getProjectedDateErrorMilestoneIds(
  milestones: MaterialDateConfirmationMilestone[],
  options: ProjectedDateValidationOptions = {}
): Set<string> {
  const errorIds = new Set<string>();

  for (let index = 1; index < milestones.length; index += 1) {
    const milestone = milestones[index];
    const maximumUpstreamBaseline = getMaximumUpstreamProjectedDateBaseline(
      milestones,
      index
    );

    if (
      isMilestoneProjectedDateInvalid(
        milestone,
        maximumUpstreamBaseline,
        options
      )
    ) {
      errorIds.add(milestone.id);
    }
  }

  return errorIds;
}

export function hasProjectedDateOrderErrors(
  milestones: MaterialDateConfirmationMilestone[],
  options: ProjectedDateValidationOptions = {}
): boolean {
  return getProjectedDateErrorMilestoneIds(milestones, options).size > 0;
}
